import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { AppSettings } from "src/app/app.settings";
import { Settings } from "src/app/app.settings.model";
import { AlertDialogComponent } from "src/app/components/alert-dialog/alert-dialog.component";
import { PromptDialogComponent } from "src/app/components/prompt-dialog/prompt-dialog.component";
import { CustomerClient, TempPasswordRequest, V3Client, WebCompany } from "src/app/models/RCCMobile.AppWebApi";
import { AuthService, SharedService } from "src/app/services";
import { TempPasswordEmailDialogComponent } from "../pages/register/temp-password-email-dialog/temp-password-email-dialog.component";
import { TheMaskedEmail } from "../pages/register/email-list-dialog/TheMaskedEmail";
import { SessionDataService } from "./session-data.service";
import { ErrorDialogComponent } from "../components/error-dialog/error-dialog.component";

@Injectable({
  providedIn: "root",
})
export class PasswordResetService
{
  public settings: Settings;
  public webEmail: string;
  public title: string;
  private dataLogical: string;
  private custId: string;
  private tempPwdAttempts: number = 0;
  private allowedTempPwdAttempts: number = 3;
  emails: string[] = [];


  constructor(
    public appSettings: AppSettings,
    public router: Router,
    public dialog: MatDialog,
    public resetDialog: MatDialog,
    private customerClient: CustomerClient,
    private sharedService: SharedService,
    private authService: AuthService,
    private sessionDataService: SessionDataService,
    public errorDialog: MatDialog,
  )
  {
    this.appSettings.settings.loadingSpinner = false;
    this.settings = this.appSettings.settings;
    this.sharedService.dataLogical.subscribe(
      (d) => (this.dataLogical = d)
    );
  }

  public resetPasswordLogic(custId?: string): void
  {
    if (this.dataLogical == null)
    {
      this.dataLogical = this.sessionDataService.DataLogical;
    }
    if (custId != undefined && custId != null && custId.length > 1)
    {
      this.custId = custId.toUpperCase();
      this.settings.loadingSpinner = true;
      this.customerClient.checkForLockedAccount(this.custId, this.dataLogical).subscribe((response) =>
      {
        if (response?.length > 1)
        {
          this.settings.loadingSpinner = false;
          this.showAlert(response)
        }
        else
        {
          this.checkAndGetCustomerEmails();
          this.settings.loadingSpinner = false;
        }
      },
        (error) =>
        {
          this.showError(error);
        }
      )
    }
    else
    {
      this.showError("Problem retrieving your information, please try again.")
    }
  }

  public checkAndGetCustomerEmails()
  {
    if (SharedService.IsNullOrEmpty(this.dataLogical))
    {
      this.dataLogical = this.sessionDataService.DataLogical;
    }
    this.customerClient.checkCustomerEmails(this.custId, this.dataLogical, "").subscribe(
      (response) =>
      {
        if (response)
        {
          this.customerClient.getResetPasswordEmails(this.custId, this.dataLogical, "").subscribe(emails =>
          {
            this.emails = emails;
            if (emails.length === 1)
            {
              let email = new TheMaskedEmail(emails[0])
              this.setTempPasswordSendEmail(email)
            } 
            else
            {
              this.openTempPWDialog();
            }
          });
        }
        else
        {
          this.settings.loadingSpinner = false;
          this.router.navigate(["/register"]);
        }
      },
      (error) =>
      {
        this.custId = "";
        if (error.toLowercase.includes("too many"))
        {
          this.settings.loadingSpinner = false;
          this.showAlert(error);
        }
      },
      () => (this.settings.loadingSpinner = false)
    );
  }

  public openTempPWDialog()
  {
    this.settings.loadingSpinner = false;
    this.resetDialog.open(TempPasswordEmailDialogComponent,
      {
        id: "temp-password-emails",
        panelClass: "form-field-less-than-180",
        maxWidth: "600px",
        maxHeight: "500px",
        data: { emails: this.emails },
      })
      .afterClosed()
      .subscribe((maskedEmailItem) =>
      {
        if (maskedEmailItem?.email == undefined || maskedEmailItem?.email == null)
        {
          this.router.navigate(["./"], { queryParams: { logical: this.dataLogical } });
        }
        else
        {
          this.setTempPasswordSendEmail(maskedEmailItem)
        }
      });
  }

  private setTempPasswordSendEmail(maskedEmail)
  {
    const tempPasswordRequest: TempPasswordRequest = {
      DataLogical: this.dataLogical,
      CustId: this.custId,
      Email: maskedEmail.email,
      CorpId: "",
    };
    this.settings.loadingSpinner = true;
    this.customerClient
      .setTempPasswordSendEmail(tempPasswordRequest)
      .subscribe(
        (res) =>
        {
          this.settings.loadingSpinner = false;
          if (res.Result)
          {
            this.promptForTempPassword(
              maskedEmail.maskedEmail,
              this.custId
            );
          }
          else
          {
            this.custId = "";
          }
        },
        (error) =>
        {
          this.dialog.open(AlertDialogComponent, {
            id: "error-",
          });
          console.error(
            "Error in set temp password send email route " +
            error
          );
          this.custId = "";
        });
  }

  private promptForTempPassword(maskedEmail: string, custId: string)
  {
    this.settings.loadingSpinner = false;
    const dialogRef = this.resetDialog.open(PromptDialogComponent, {
      id: "password-verify-code",
      panelClass: "form-field-less-than-180",
      maxWidth: "600px",
      maxHeight: "500px",
      disableClose: true,

      data: {
        title: "Verification Code",
        message: "Check " + maskedEmail + " for the verification code",
        buttonText: "Done",
        placeholder: "Code",
        label: "Code",
        type: "number",
        showClose: false
      },
    });
    dialogRef.afterClosed().subscribe((result) =>
    {
      if (result != null)
      {
        this.sharedService.tempPassword = result;
        const authInfo = {
          IsCustomer: true,
          UserName: custId,
          CustID: "",
          DataLogical: this.dataLogical,
          Password: result,
        };
        this.settings.loadingSpinner = true;

        this.authService.login(authInfo, false).subscribe(
          (_) =>
          {
            this.settings.loadingSpinner = false;
            this.router.navigate(["home"]);
            this.tempPwdAttempts = 0;
          },
          (error) =>
          {
            this.settings.loadingSpinner = false;
            if (error.toLowerCase().includes("invalid login"))
            {
              this.tempPwdAttempts++;
              if (this.tempPwdAttempts <= this.allowedTempPwdAttempts)
              {
                this.promptForTempPassword(maskedEmail, custId);
              }
            }
            else if (error.toLowerCase().includes("too many"))
            {
              this.showAlert(error)
            }
          }
        );
      }
      else
      {
        return;
      }
    });
  }

  public showError(err: string): void
  {
    const msg = err;
    this.errorDialog.open(ErrorDialogComponent, {
      minWidth: "360px",
      data: err
    });
  }

  public showAlert(msg: string): void
  {
    this.dialog.open(AlertDialogComponent, {
      minWidth: "360px",
      disableClose: true,
      data: {
        title: "Account Locked",
        message: msg,
        buttonText: "Close"
      }
    });
  }
}
